import { useQuery } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import { StatCard } from '@/components/ui/Card';
import Spinner from '@/components/ui/Spinner';
import { getValidationBoard } from '@/api/cashflows';
import { listExchangeRuns } from '@/api/exchangeRuns';
import { getMappingStatus } from '@/api/cfCodes';

export default function DashboardPage() {
  const { data: board, isLoading: boardLoading } = useQuery({
    queryKey: ['validation-board'],
    queryFn: () => getValidationBoard(),
    // Keep dashboard stats live as other users submit/agree.
    refetchInterval: 30_000,
    refetchOnWindowFocus: true,
    staleTime: 15_000,
  });

  const { data: runs, isLoading: runsLoading } = useQuery({
    queryKey: ['exchange-runs'],
    queryFn: listExchangeRuns,
  });

  // CF codes missing from the Qlik report mapping (figures on them never reach Qlik).
  const { data: mapping } = useQuery({
    queryKey: ['cf-code-mapping-status'],
    queryFn: getMappingStatus,
    refetchInterval: 60_000,
  });
  const unmappedCount = mapping?.unmapped.length ?? 0;
  const exposureGbp = mapping ? Number(mapping.exposure.absPence) / 100 : 0;

  const lastRun = runs?.[0];

  const totalEntities = board?.summary.totalEntities ?? 0;
  const greenEntities = board?.summary.greenEntities ?? 0;
  const pendingEntities = totalEntities - greenEntities;

  // CF9999 balance: sum across all entities (a proxy for unclassified)
  // Real data comes from the board; here we just show the count of unbalanced
  const unbalancedCount = board?.divisions
    .flatMap((d) => d.entities)
    .flatMap((e) => Object.values(e.cells))
    .filter((c) => !c.isBalanced || !c.cf9999IsZero).length ?? 0;

  const lastSyncTime = lastRun?.completedAt
    ? new Date(lastRun.completedAt).toLocaleString('en-GB', {
        day: '2-digit',
        month: 'short',
        hour: '2-digit',
        minute: '2-digit',
      })
    : 'Never';

  return (
    <div className="space-y-8">
      <div>
        <h1 className="text-2xl font-bold text-gray-900">System Dashboard</h1>
        <p className="mt-1 text-sm text-gray-500">
          System status at a glance: entity progress, Qlik sync health and code-mapping checks.
        </p>
      </div>

      {/* Stats row */}
      {boardLoading || runsLoading ? (
        <div className="flex justify-center py-12">
          <Spinner size="lg" />
        </div>
      ) : (
        <div className="grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-4">
          <StatCard
            title="Entities pending"
            value={pendingEntities}
            color="amber"
            icon={
              <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
            }
          />
          <StatCard
            title="Entities agreed"
            value={`${greenEntities} / ${totalEntities}`}
            color="green"
            icon={
              <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
            }
          />
          <StatCard
            title="Unbalanced periods"
            value={unbalancedCount}
            color={unbalancedCount > 0 ? 'danger' : 'green'}
            icon={
              <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
              </svg>
            }
          />
          {mapping?.capturedAt && (
            <Link to="/admin/cf-codes" className="block focus:outline-none focus-visible:ring-2 focus-visible:ring-primary-500 rounded-xl">
              <StatCard
                title="Codes not in Qlik mapping"
                value={unmappedCount}
                color={unmappedCount > 0 ? 'danger' : 'green'}
                subtitle={
                  unmappedCount > 0
                    ? `${mapping.exposure.lines} submitted line${mapping.exposure.lines === 1 ? '' : 's'} · £${exposureGbp.toLocaleString('en-GB', { maximumFractionDigits: 0 })} not reaching Qlik`
                    : 'All classified codes mapped'
                }
                icon={
                  <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9.172 16.172a4 4 0 015.656 0M9 10h.01M15 10h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                }
              />
            </Link>
          )}
          <StatCard
            title="Last Qlik sync"
            value={lastSyncTime}
            color="primary"
            subtitle={lastRun?.outcome ?? '—'}
            icon={
              <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
              </svg>
            }
          />
        </div>
      )}

      {/* Quick links */}
      <div className="grid grid-cols-1 gap-5 sm:grid-cols-2">
        <Link
          to="/validation"
          className="group rounded-xl border border-gray-200 bg-white p-6 shadow-sm transition hover:border-primary-300 hover:shadow-md"
        >
          <h2 className="text-base font-semibold text-gray-900 group-hover:text-primary-700">
            Validation Board
          </h2>
          <p className="mt-1 text-sm text-gray-500">
            See which entities and periods are green across all divisions.
          </p>
        </Link>
        <Link
          to="/entities"
          className="group rounded-xl border border-gray-200 bg-white p-6 shadow-sm transition hover:border-primary-300 hover:shadow-md"
        >
          <h2 className="text-base font-semibold text-gray-900 group-hover:text-primary-700">
            Entity Cash Flows
          </h2>
          <p className="mt-1 text-sm text-gray-500">
            Enter or review classified lines for each entity.
          </p>
        </Link>
      </div>

      {/* Recent exchange runs */}
      {runs !== undefined && (
        <div>
          <h2 className="mb-3 text-base font-semibold text-gray-900">Recent sync runs</h2>
          {runs.length === 0 ? (
            <p className="text-sm text-gray-500">No sync runs yet.</p>
          ) : (
            <div className="overflow-hidden rounded-xl border border-gray-200 bg-white">
              <table className="min-w-full divide-y divide-gray-100 text-sm">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-4 py-2 text-left text-xs font-semibold uppercase tracking-wide text-gray-500">Direction</th>
                    <th className="px-4 py-2 text-left text-xs font-semibold uppercase tracking-wide text-gray-500">Periods</th>
                    <th className="px-4 py-2 text-left text-xs font-semibold uppercase tracking-wide text-gray-500">Rows</th>
                    <th className="px-4 py-2 text-left text-xs font-semibold uppercase tracking-wide text-gray-500">Outcome</th>
                    <th className="px-4 py-2 text-left text-xs font-semibold uppercase tracking-wide text-gray-500">Completed</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-50">
                  {runs.slice(0, 5).map((run) => (
                    <tr key={run.id}>
                      <td className="px-4 py-2 font-medium text-gray-700">{run.direction}</td>
                      <td className="px-4 py-2 text-gray-500">{run.periods.join(', ')}</td>
                      <td className="px-4 py-2 text-gray-500">{run.rowCount}</td>
                      <td className="px-4 py-2">
                        <span
                          className={
                            run.outcome === 'SUCCESS'
                              ? 'text-green-700'
                              : run.outcome === 'PARTIAL'
                                ? 'text-amber-700'
                                : 'text-red-700'
                          }
                        >
                          {run.outcome}
                        </span>
                      </td>
                      <td className="px-4 py-2 text-gray-500">
                        {run.completedAt
                          ? new Date(run.completedAt).toLocaleString('en-GB')
                          : 'Running…'}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
